import type React from 'react';

import { useGetEvent } from '@vlabs/api/luna-api/client';
import type { EventId } from '@vlabs/api/luna-api/model';
import { DetectionIcon } from '@vlabs/icons';
import HandlerLink from '@vlabs/shared/components/handler-link/HandlerLink';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { EventStreamLink } from '@vlabs/shared/components/stream-link/EventStreamLink';
import { calcTagType } from '@vlabs/shared/utils';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import cn from 'classnames';

import {
  EventBodyTopMatchSample,
  EventFaceTopMatchSample,
} from '../detection-sample';
import { useTopMatchEventInfoTranslation } from './i18n';
import st from './TopMatchEventInfo.module.sass';
import { buildTestid } from '../../../shared/lib/utils';

interface TopMatchEventInfoProps extends WithClassNameProp, WithDataTestid {
  eventId: EventId;
  similarity: number;
}

const displayName = 'TopMatchEventInfo';
const testid = buildTestid(displayName);

function formatValue(value?: string | null): React.ReactNode {
  if (!value) return <span className={st.Empty}>-</span>;
  return value;
}

function TopMatchEventInfo({
  eventId,
  similarity,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: TopMatchEventInfoProps): React.JSX.Element | null {
  const { t } = useTopMatchEventInfoTranslation();
  const { data: event } = useGetEvent(eventId);

  if (!event) return null;

  const hasFaceDetections = !!event.face_detections?.length;
  const hasBodyDetections = !!event.body_detections?.length;
  const shouldShowFaceSample = hasFaceDetections || !hasBodyDetections;
  const similarityKind = calcTagType(similarity);

  return (
    <div
      className={cn(
        st.TopMatchEventInfo,
        similarityKind && st[`TopMatchEventInfo_${similarityKind}`],
        className,
      )}
      {...testid(dataTestId)}
    >
      <div
        className={st.Samples}
        {...testid('Samples', dataTestId)}
      >
        {shouldShowFaceSample ? (
          <EventFaceTopMatchSample
            className={st.FaceSample}
            matchedEventId={eventId}
            size="large"
            {...testid('FaceSample', dataTestId)}
          />
        ) : null}
        {hasBodyDetections ? (
          <EventBodyTopMatchSample
            className={st.BodySample}
            matchedEventId={eventId}
            size="large"
            {...testid('BodySample', dataTestId)}
          />
        ) : null}
      </div>

      <div className={st.Content}>
        <SimilarityTag
          icon={<DetectionIcon />}
          size="medium"
          title={t('similarity')}
          value={similarity}
          {...testid('SimilarityTag', dataTestId)}
        />

        <div className={st.Rows}>
          <div className={st.Row}>
            <span className={st.RowTitle}>{t('create_time')}</span>
            <span className={st.RowValue}>{formatValue(formatDateTime(event.create_time, DATE_FORMATS.withYear))}</span>
          </div>
          <div className={st.Row}>
            <span className={st.RowTitle}>{t('source')}</span>
            <span className={st.RowValue}>
              <EventStreamLink
                kind="secondary"
                streamId={event.stream_id}
              />
            </span>
          </div>
          <div className={st.Row}>
            <span className={st.RowTitle}>{t('handler')}</span>
            <span className={st.RowValue}>
              <HandlerLink
                handlerId={event.handler_id}
                kind="secondary"
              />
            </span>
          </div>
          <div className={st.Row}>
            <span className={st.RowTitle}>{t('location')}</span>
            <span className={st.RowValue}>{formatValue(buildLocationString(event))}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

TopMatchEventInfo.displayName = displayName;

export { TopMatchEventInfo };
